import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TASK_STATUSES, type Label, type Member, type TaskStatus } from '@hive/shared';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import type { GridTask } from '@/data/tasks';
import { boardCollision } from './boardCollision';
import { planDrop, type Columns, type SortKey } from './model';
import { PriorityMark, TaskMeta } from './TaskBits';

const DONE_LIMIT = 50;

interface CardProps {
  task: GridTask;
  labels: readonly Label[];
  members: readonly Member[];
  subtaskCount: number;
  today: string;
  onOpen?: (taskId: string) => void;
}

function Card({ task, labels, members, subtaskCount, today, onOpen, dragging }: CardProps & { dragging?: boolean }) {
  return (
    <div
      className={cn(
        'cut-tr flex flex-col gap-2 bg-bg-2 p-3 shadow-[inset_0_0_0_1px_var(--color-line)]',
        dragging && 'shadow-[inset_0_0_0_1px_var(--color-cyan),0_0_18px_rgb(0_240_255/0.25)]',
      )}
    >
      <div className="flex items-start gap-2">
        <PriorityMark priority={task.priority} />
        <button
          type="button"
          className="min-w-0 flex-1 cursor-pointer text-left leading-snug font-semibold hover:text-cyan"
          onClick={() => onOpen?.(task.id)}
        >
          {task.title}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 empty:hidden">
        <TaskMeta task={task} labels={labels} members={members} subtaskCount={subtaskCount} today={today} />
      </div>
    </div>
  );
}

function SortableCard(props: CardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task.id,
    data: { type: 'task' },
  });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('touch-none', isDragging && 'opacity-30')}
      {...attributes}
      {...listeners}
      aria-roledescription="Draggable task"
    >
      <Card {...props} />
    </li>
  );
}

function Column({
  status,
  name,
  count,
  children,
}: {
  status: TaskStatus;
  name: string;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${status}`, data: { type: 'column', status } });
  return (
    <section
      ref={setNodeRef}
      aria-label={name}
      className={cn(
        'flex w-72 shrink-0 flex-col bg-bg/60 shadow-[inset_0_0_0_1px_var(--color-line)]',
        isOver && 'shadow-[inset_0_0_0_1px_var(--color-cyan)]',
      )}
    >
      <h2 className="flex items-center justify-between border-b border-line px-3 py-2 font-display text-[11px] font-bold tracking-[0.16em] text-cyan uppercase">
        {name}
        <span className="font-mono text-muted">{count}</span>
      </h2>
      {children}
    </section>
  );
}

/** Grid's kanban view. Drag cards between columns; in manual order, also within one. */
export function TaskBoard({
  columns,
  sort,
  labels,
  members,
  subtasks,
  today,
  onOpen,
  onMove,
}: {
  columns: Columns;
  sort: SortKey;
  labels: readonly Label[];
  members: readonly Member[];
  subtasks: Map<string, GridTask[]>;
  today: string;
  onOpen: (taskId: string) => void;
  onMove: (task: GridTask, change: { status: TaskStatus; position?: string }) => void;
}) {
  const sensors = useSensors(
    // A small movement threshold, so a click still opens the card.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showAllDone, setShowAllDone] = useState(false);
  const all = TASK_STATUSES.flatMap((s) => columns[s.id]);
  const collision = useMemo(
    () => boardCollision(new Map(TASK_STATUSES.map((s) => [s.id, columns[s.id].map((t) => t.id)]))),
    [columns],
  );
  const active = activeId ? all.find((t) => t.id === activeId) : undefined;
  const cardProps = (task: GridTask) => ({
    task,
    labels,
    members,
    subtaskCount: subtasks.get(task.id)?.length ?? 0,
    today,
  });

  function handleDragEnd({ active: dragged, over }: DragEndEvent) {
    setActiveId(null);
    if (!over) return;
    const data = over.data.current as { type?: string; status?: TaskStatus } | undefined;
    const target = data?.type === 'column' ? { column: data.status } : { taskId: String(over.id) };
    const change = planDrop(columns, String(dragged.id), target, sort);
    const task = all.find((t) => t.id === dragged.id);
    if (change && task) onMove(task, change);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={({ active: a }) => setActiveId(String(a.id))}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-4">
        {TASK_STATUSES.map((status) => {
          const tasks = status.id === 'done' && !showAllDone ? columns.done.slice(0, DONE_LIMIT) : columns[status.id];
          return (
            <Column key={status.id} status={status.id} name={status.name} count={columns[status.id].length}>
              <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                <ul className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto p-2">
                  {tasks.map((task) => (
                    <SortableCard key={task.id} {...cardProps(task)} onOpen={onOpen} />
                  ))}
                </ul>
              </SortableContext>
              {status.id === 'done' && columns.done.length > DONE_LIMIT && !showAllDone ? (
                <button
                  type="button"
                  className="cursor-pointer border-t border-line px-3 py-2 text-left font-mono text-xs text-cyan hover:underline"
                  onClick={() => setShowAllDone(true)}
                >
                  Show all {columns.done.length}
                </button>
              ) : null}
            </Column>
          );
        })}
      </div>
      <DragOverlay>{active ? <Card {...cardProps(active)} dragging /> : null}</DragOverlay>
    </DndContext>
  );
}
